import { v4 as uuid } from 'uuid';

export const generateId = (): string => {
  // in some cases uuid dosn't work as expected so the timestamp is added.
  return uuid() + new Date().getTime();
};

export const generateStyleString = (
  styleObject: Record<string, string>
): string => {
  return Object.keys(styleObject).reduce((acc, key) => {
    acc += `${key}:${styleObject[key]};`;
    return acc;
  }, '');
};

export const camelCaseToUnderscore = (str: string): string => {
  return str
    .split(/(?=[A-Z])/)
    .join('_')
    .toLowerCase();
};

export const encodeToBase64 = (source: Record<string, any>): string => {
  const objJsonStr = JSON.stringify(source);

  return btoa(objJsonStr);
};

export const fromBase64 = (source: string): any => {
  const decoded = atob(source);

  return JSON.parse(decoded);
};

export const slugifyText = (name: string): string => {
  return name
    .replace(/[^\w\s]|/g, '')
    .split(/\s|_/)
    .map(
      (word, i) =>
        word &&
        (i === 0
          ? word.toLowerCase()
          : word[0]?.toUpperCase() + word.slice(1).toLowerCase())
    )
    .join('');
};

export const encodeBase64 = (str: string): string => {
  return btoa(unescape(encodeURIComponent(str)));
};

export const decodeBase64 = (str: string): string => {
  return decodeURIComponent(escape(atob(str)));
};

export const capitalize = (str: string) => {
  if (typeof str !== 'string') {
    return '';
  }
  return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
};

export const formatPlural = (word: string, count: number) =>
  count === 1 ? word : `${word}s`;

// Convert camelCase to kebab-case (for css)
// Origin: https://1loc.dev/string/convert-camel-case-to-kebab-case-and-vice-versa/
export const camelCaseToKebabCase = (str: string): string => {
  if (!str) {
    return '';
  }
  return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
};

// Origin: https://1loc.dev/string/convert-camel-case-to-kebab-case-and-vice-versa/
export const kebabCaseToCamelCase = (str: string): string => {
  if (!str) {
    return '';
  }
  return str.replace(/-./g, (m) => m.toUpperCase()[1]);
};

const replaceSingleNbspsOnly = new RegExp(/(?!:&nbsp{2,})(?:&nbsp;)/);

const cache = new Map<string, string>();
const MAP_THRESHOLD = 4000;

// This method take care of condencing any amount of whitespaces and single occurrences of &nbsp;
// into a single space between words inside the string we are looking at
export const condenseWhiteSpace = (
  value = '',
  onValueAddedToCache?: () => void,
  onCacheCleared?: () => void
): string => {
  if (value === '' || value === ' ') return value;

  if (cache.has(value)) {
    return cache.get(value) as string;
  }

  if (cache.size > MAP_THRESHOLD) {
    cache.clear();
    onCacheCleared?.();
  }

  // Replace only occurrences that the user used on &nbsp;
  let replacedValue = value.replace(replaceSingleNbspsOnly, ' ').trim(); // Replace only single &nbsp; character with spaces
  replacedValue = replacedValue.replace(/\s+/g, ' '); // Replace all white spaces with a single space
  cache.set(value, replacedValue);
  onValueAddedToCache?.();
  return replacedValue;
};

export function createComparableString(str) {
  return str.toLowerCase().replace(/\s/g, '');
}

export const splitBylines = (str, lines) => {
  const breakLineArr: string[] = str.split('\n');
  const linesBlockArr: string[] = [];
  while (breakLineArr.length) {
    linesBlockArr.push(breakLineArr.splice(0, lines).join('\n'));
  }
  return linesBlockArr;
};
